import { LENS_CARD_TYPES, type LensAggregate } from '~/types/leLens';
import type {
  LensCardSize,
  LensCardType,
  LensCardView,
  LensDatasetField,
  LensFilter,
  LensValueFormat,
} from '~/types/leLens';

import { prepareFields } from './datasetFields';
import type { VerifiedMetric, VerifiedSource } from './prompts';

// Derived, not restated: a local list silently degraded every newer type to
// a table, so a planned scatter or pie never reached the dashboard.
const CARD_TYPES: ReadonlyArray<LensCardType> = LENS_CARD_TYPES;
const CARD_SIZES: Array<LensCardSize> = ['small', 'medium', 'large', 'full'];

const MAX_SECTIONS = 6;
const MAX_CARDS = 16;

/**
 * The columns a dashboard filter already expects on every card.
 *
 * Setting a filter's column auto-binds it across the whole Lens (see
 * filterBindings.ts), so that column has to exist in a new card's result for
 * the binding to work. A filter with no column is bound by hand per card
 * instead, against whatever the query happens to return, so it names nothing
 * a new query needs to pre-empt.
 */
export function filterableColumns(filters: Array<LensFilter>): Array<string> {
  return filters.map((filter) => filter.column).filter((column): column is string => Boolean(column));
}

/**
 * Wraps an async action so a call arriving while a previous one is still
 * running is dropped rather than started concurrently.
 *
 * Backed by a ref rather than component state, because a disabled button only
 * takes effect once React commits the render that disabled it, which is not
 * the same instant as the click that triggers it. A second call landing in
 * that gap needs something that changes immediately, not on the next render.
 */
export function guardReentry(running: { current: boolean }, action: () => Promise<void>): () => Promise<void> {
  return async () => {
    if (running.current) {
      return;
    }
    running.current = true;
    try {
      await action();
    } finally {
      running.current = false;
    }
  };
}

export type PlannedCard = {
  title: string;
  type: LensCardType;
  size: LensCardSize;
  /** The single-card request handed to AskRichard in the second phase. */
  request: string;
  format?: LensValueFormat;
};

export type PlannedSection = { title: string; cards: Array<PlannedCard> };

export type DashboardPlan = { sections: Array<PlannedSection> };

/**
 * The plan arrives in the answer text, which also carries thinking, so every
 * fenced JSON block is tried and the last valid one wins: the finished plan
 * comes after any reasoning that resembles one.
 */
export function extractPlan(text: string): DashboardPlan | null {
  const candidates = [...collectFencedBlocks(text), ...collectBareObjects(text)];
  let plan: DashboardPlan | null = null;
  for (const candidate of candidates) {
    const parsed = safeParse(candidate);
    const normalised = parsed && normalisePlan(parsed);
    if (normalised) {
      plan = normalised;
    }
  }
  return plan;
}

function collectFencedBlocks(text: string): Array<string> {
  const blocks: Array<string> = [];
  const fence = /```(?:json)?\s*\n?([\s\S]*?)```/gi;
  let match = fence.exec(text);
  while (match) {
    blocks.push(match[1]);
    match = fence.exec(text);
  }
  return blocks;
}

/** A model that answers without fencing still tends to emit one object. */
function collectBareObjects(text: string): Array<string> {
  const first = text.indexOf('{');
  const last = text.lastIndexOf('}');
  return first === -1 || last <= first ? [] : [text.slice(first, last + 1)];
}

function safeParse(value: string): unknown {
  try {
    return JSON.parse(value.trim());
  } catch {
    return null;
  }
}

function normalisePlan(value: unknown): DashboardPlan | null {
  if (!isRecord(value) || !Array.isArray(value.sections)) {
    return null;
  }

  let budget = MAX_CARDS;
  const sections: Array<PlannedSection> = [];

  for (const rawSection of value.sections.slice(0, MAX_SECTIONS)) {
    if (!isRecord(rawSection) || !Array.isArray(rawSection.cards)) {
      continue;
    }
    const cards: Array<PlannedCard> = [];

    for (const rawCard of rawSection.cards) {
      if (budget <= 0) {
        break;
      }
      if (!isRecord(rawCard)) {
        continue;
      }
      const request = text(rawCard.request) || text(rawCard.title);
      if (!request) {
        continue;
      }

      // Size from the resolved type, not the requested one: a type that
      // degraded to a table should be sized like a table.
      const type = CARD_TYPES.includes(rawCard.type as LensCardType) ? (rawCard.type as LensCardType) : 'table';
      const format = normaliseFormat(rawCard.format);

      cards.push({
        title: (text(rawCard.title) || request).slice(0, 120),
        type,
        size: CARD_SIZES.includes(rawCard.size as LensCardSize) ? (rawCard.size as LensCardSize) : defaultSize(type),
        request,
        ...(format ? { format } : {}),
      });
      budget -= 1;
    }

    if (cards.length) {
      sections.push({ title: (text(rawSection.title) || '').slice(0, 120), cards });
    }
  }

  return sections.length ? { sections } : null;
}

/** A metric is one number, so it does not need a two-per-row slot. */
function defaultSize(type: LensCardType): LensCardSize {
  if (type === 'metric') {
    return 'small';
  }
  // A two-axis grid is dense: give it the same room as a table or a stack.
  if (type === 'table' || type === 'stacked' || type === 'area' || type === 'combo' || type === 'heatmap') {
    return 'full';
  }
  if (type === 'pie') {
    return 'medium';
  }
  return 'large';
}

function normaliseFormat(value: unknown): LensValueFormat | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const kind = value.kind;
  if (kind !== 'number' && kind !== 'currency' && kind !== 'percent') {
    return undefined;
  }
  const currency = text(value.currency)?.toUpperCase();
  if (kind === 'currency' && (!currency || !/^[A-Z]{3}$/.test(currency))) {
    return { kind: 'currency', currency: 'AUD' };
  }
  const decimals = typeof value.decimals === 'number' && Number.isInteger(value.decimals) ? value.decimals : undefined;
  return {
    kind,
    ...(currency && kind === 'currency' ? { currency } : {}),
    ...(decimals !== undefined && decimals >= 0 && decimals <= 6 ? { decimals } : {}),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/** Ids must be unique across the whole Lens, not just within the plan. */
export function makeUniqueId(source: string, taken: Set<string>, fallback: string): string {
  const base =
    source
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .replace(/^([^a-z])/, `${fallback}-$1`)
      .slice(0, 58) || fallback;
  let id = base;
  let counter = 2;
  while (taken.has(id)) {
    id = `${base}-${counter}`;
    counter += 1;
  }
  taken.add(id);
  return id;
}

export type PlannedDataset = {
  key: string;
  name: string;
  grain: string;
  columns: Array<string>;
  request: string;
  /** Columns to offer as selection controls. */
  dimensions?: Array<string>;
  /** Columns computed from the ones the query returns. */
  fields?: Array<LensDatasetField>;
};

export type PlannedAssignment = {
  /** 1-based position in the planned card list. */
  index: number;
  datasetKey: string;
  view?: LensCardView;
};

export type Consolidation = {
  datasets: Array<PlannedDataset>;
  assignments: Array<PlannedAssignment>;
  /** Absent when the model declared none, and when a model never chose the grouping at all. */
  sources?: ReadonlyArray<VerifiedSource>;
};

const MAX_SOURCES = 8;
const MAX_SOURCE_COLUMNS = 80;
const MAX_SOURCE_METRICS = 12;

const MAX_DATASETS = 4;
const MAX_FIELDS = 12;
export const AGGREGATES: ReadonlyArray<LensAggregate> = ['sum', 'avg', 'count', 'countDistinct', 'min', 'max', 'first'];

/**
 * Reads the data model proposed for a planned dashboard. Like the plan it
 * arrives in answer text, so every fenced block is tried and the last valid
 * one wins.
 */
/** The tables the discovery turn found, read out of its reply. */
export function extractSources(text: string): Array<VerifiedSource> {
  const candidates = [...collectFencedBlocks(text), ...collectBareObjects(text)];
  let found: Array<VerifiedSource> = [];
  for (const candidate of candidates) {
    const parsed = safeParse(candidate);
    const sources = isRecord(parsed) ? normaliseSources(parsed.sources) : [];
    if (sources.length) {
      found = sources;
    }
  }
  return found;
}

export function extractConsolidation(text: string, cardCount: number): Consolidation | null {
  const candidates = [...collectFencedBlocks(text), ...collectBareObjects(text)];
  let result: Consolidation | null = null;
  for (const candidate of candidates) {
    const parsed = safeParse(candidate);
    const normalised = parsed && normaliseConsolidation(parsed, cardCount);
    if (normalised) {
      result = normalised;
    }
  }
  return result;
}

/**
 * Calculated fields as the model declared them, kept only where the formula
 * reads and reads nothing the dataset does not return.
 *
 * A field is checked here as well as at render time because a field that cannot
 * be computed is a card that renders empty, and the reader would be looking at
 * a blank chart rather than at a message about a formula.
 */
function normaliseFields(value: unknown, columns: Array<string>): Array<LensDatasetField> {
  if (!Array.isArray(value)) {
    return [];
  }

  const proposed: Array<LensDatasetField> = [];
  for (const raw of value.slice(0, MAX_FIELDS)) {
    if (!isRecord(raw)) {
      continue;
    }
    const name = text(raw.name);
    const expression = text(raw.expression);
    if (!name || !expression) {
      continue;
    }
    proposed.push({
      name,
      expression,
      // A rate declared as a row field is the error this whole mechanism
      // exists to prevent, but defaulting to 'aggregate' would silently
      // reinterpret a genuine per-row calculation, so the declaration stands.
      when: text(raw.when) === 'row' ? 'row' : 'aggregate',
    });
  }

  const prepared = prepareFields(proposed, columns);
  return [...prepared.row, ...prepared.aggregate].map((entry) => entry.field);
}

function normaliseConsolidation(value: unknown, cardCount: number): Consolidation | null {
  if (!isRecord(value) || !Array.isArray(value.datasets)) {
    return null;
  }

  const datasets: Array<PlannedDataset> = [];
  for (const raw of value.datasets.slice(0, MAX_DATASETS)) {
    if (!isRecord(raw)) {
      continue;
    }
    const key = text(raw.key);
    const name = text(raw.name);
    const request = text(raw.request) || name;
    const columns = Array.isArray(raw.columns) ? raw.columns.map(text).filter(Boolean) : [];
    // A dataset with no declared columns has no contract, so a card's view
    // would reference names nothing guarantees.
    if (!key || !name || !request || columns.length === 0) {
      continue;
    }
    // A dimension must be a column the dataset promised, or its control would
    // offer values nothing carries.
    const dimensions = Array.isArray(raw.dimensions)
      ? raw.dimensions.map(text).filter((column): column is string => Boolean(column && columns.includes(column)))
      : [];

    const fields = normaliseFields(raw.fields, columns);

    datasets.push({
      key,
      name,
      grain: text(raw.grain) || 'one row per group',
      columns,
      request,
      ...(dimensions.length ? { dimensions } : {}),
      ...(fields.length ? { fields } : {}),
    });
  }
  if (!datasets.length) {
    return null;
  }

  const keys = new Set(datasets.map((dataset) => dataset.key));
  const assignments: Array<PlannedAssignment> = [];
  const claimed = new Set<number>();

  for (const raw of Array.isArray(value.cards) ? value.cards : []) {
    if (!isRecord(raw)) {
      continue;
    }
    const index = Number(raw.index);
    const datasetKey = text(raw.datasetKey);
    if (!Number.isInteger(index) || index < 1 || index > cardCount) {
      continue;
    }
    if (!datasetKey || !keys.has(datasetKey) || claimed.has(index)) {
      continue;
    }
    claimed.add(index);
    assignments.push({
      index,
      datasetKey,
      ...(normaliseView(
        raw.view,
        datasets.find((dataset) => dataset.key === datasetKey),
      ) ?? {}),
    });
  }

  // Datasets nothing reads would be queried for no one.
  const used = new Set(assignments.map((assignment) => assignment.datasetKey));
  const sources = normaliseSources(value.sources);
  return assignments.length
    ? {
        datasets: datasets.filter((dataset) => used.has(dataset.key)),
        assignments,
        ...(sources.length ? { sources } : {}),
      }
    : null;
}

/** Tables as the model named them, kept only where a name and columns are both there to pass on. */
function normaliseSources(value: unknown): Array<VerifiedSource> {
  if (!Array.isArray(value)) {
    return [];
  }
  const sources: Array<VerifiedSource> = [];
  for (const raw of value.slice(0, MAX_SOURCES)) {
    if (!isRecord(raw)) {
      continue;
    }
    const table = text(raw.table);
    const columns = Array.isArray(raw.columns)
      ? raw.columns.slice(0, MAX_SOURCE_COLUMNS).map(text).filter(Boolean)
      : [];
    if (!table || !columns.length) {
      continue;
    }
    const date = text(raw.date);
    const metrics = normaliseMetrics(raw.metrics);
    sources.push({ table, columns, ...(date ? { date } : {}), ...(metrics.length ? { metrics } : {}) });
  }
  return sources;
}

/**
 * Certified measures as the discovery turn reported them.
 *
 * An expression is required and an id is not decoration: a measure with no
 * expression carries no definition, and handing one forward would let a card
 * claim to be certified while the query underneath it was invented.
 */
function normaliseMetrics(value: unknown): Array<VerifiedMetric> {
  if (!Array.isArray(value)) {
    return [];
  }
  const metrics: Array<VerifiedMetric> = [];
  for (const raw of value.slice(0, MAX_SOURCE_METRICS)) {
    if (!isRecord(raw)) {
      continue;
    }
    const id = text(raw.id);
    const expression = text(raw.expression);
    if (!id || !expression) {
      continue;
    }
    const avoid = Array.isArray(raw.avoid) ? raw.avoid.map(text).filter(Boolean) : [];
    metrics.push({ id, expression, ...(avoid.length ? { avoid } : {}) });
  }
  return metrics;
}

/** A view may only name columns the dataset promised to return. */
function normaliseView(value: unknown, dataset: PlannedDataset): { view: LensCardView } | null {
  if (!isRecord(value)) {
    return null;
  }
  // A calculated field is a column as far as a card is concerned: it is the
  // whole reason the dataset declared one. Left out, a rate card assigned to
  // the field it needs loses its measure here and falls back to a query of its
  // own, which is the cost this was meant to remove.
  const known = new Set([...dataset.columns, ...(dataset.fields ?? []).map((field) => field.name)]);
  const view: LensCardView = {};

  const measure = text(value.value);
  if (measure && !known.has(measure)) {
    return null;
  }

  for (const key of ['x', 'series', 'value'] as const) {
    const column = text(value[key]);
    if (column && known.has(column)) {
      view[key] = column;
    }
  }
  if (typeof value.aggregate === 'string' && (AGGREGATES as ReadonlyArray<string>).includes(value.aggregate)) {
    view.aggregate = value.aggregate as LensCardView['aggregate'];
  }

  // Both halves have to be columns the dataset returns. A ratio missing one of
  // them would divide by nothing and render an empty card, so it is dropped
  // rather than carried through half-formed.
  if (isRecord(value.ratio)) {
    const of = text(value.ratio.of);
    const per = text(value.ratio.per);
    if (of && per && known.has(of) && known.has(per)) {
      view.ratio = { of, per };
    }
  }

  return Object.keys(view).length ? { view } : null;
}

/**
 * The projection of the last top-level SELECT: what the query actually
 * returns. Depth-aware, because a probe wraps the real query in a subquery and
 * so mentions every column while returning none of them.
 */
export function outerProjection(sql: string): string {
  const body = sql.replace(/--[^\n]*/g, ' ').replace(/\/\*[\s\S]*?\*\//g, ' ');
  let depth = 0;
  let quote: string | null = null;
  let lastSelect = -1;
  const tops: Array<{ select: number; from: number }> = [];

  for (let index = 0; index < body.length; index += 1) {
    const char = body[index];
    if (quote) {
      if (char === quote) {
        quote = null;
      }
      continue;
    }
    if (char === "'" || char === '"' || char === '`') {
      quote = char;
      continue;
    }
    if (char === '(') {
      depth += 1;
    } else if (char === ')') {
      depth -= 1;
    } else if (depth === 0) {
      if (/^select\b/i.test(body.slice(index, index + 7)) && /(^|[\s(),])/.test(body[index - 1] ?? ' ')) {
        lastSelect = index + 6;
      } else if (
        lastSelect !== -1 &&
        /^from\b/i.test(body.slice(index, index + 5)) &&
        /\s/.test(body[index - 1] ?? ' ')
      ) {
        tops.push({ select: lastSelect, from: index });
        lastSelect = -1;
      }
    }
  }

  const last = tops[tops.length - 1];
  return last ? body.slice(last.select, last.from) : body;
}

/** What the last top-level SELECT reads from, which a `*` is transparent to. */
export function outerSource(sql: string): string {
  const body = stripComments(sql);
  const projection = outerProjection(sql);
  const at = body.lastIndexOf(projection);
  if (at === -1) {
    return '';
  }
  // Past the projection sits FROM, then the relation this select reads.
  return body
    .slice(at + projection.length)
    .replace(/^\s*from\b/i, '')
    .trim();
}

function stripComments(sql: string): string {
  return sql.replace(/--[^\n]*/g, ' ').replace(/\/\*[\s\S]*?\*\//g, ' ');
}

/** A projection that only forwards its source: `*`, `t.*`, or several of them. */
const STAR_ONLY = /^\s*(?:(?:[A-Za-z_][A-Za-z0-9_]*\s*\.)?\*\s*,?\s*)+$/;

/** The text inside the parentheses opening at `from`, or '' if unbalanced. */
function balanced(body: string, from: number): string {
  let depth = 0;
  let quote: string | null = null;
  for (let index = from; index < body.length; index += 1) {
    const char = body[index];
    if (quote) {
      if (char === quote) {
        quote = null;
      }
      continue;
    }
    if (char === "'" || char === '"' || char === '`') {
      quote = char;
    } else if (char === '(') {
      depth += 1;
    } else if (char === ')') {
      depth -= 1;
      if (depth === 0) {
        return body.slice(from + 1, index);
      }
    }
  }
  return '';
}

/** Named subqueries in a leading WITH clause, by name. */
export function commonTableExpressions(sql: string): Map<string, string> {
  const body = stripComments(sql);
  const found = new Map<string, string>();
  const withAt = /\bwith\b/i.exec(body);
  if (!withAt) {
    return found;
  }

  const pattern = /(?:^|[\s,(])`?([A-Za-z_][A-Za-z0-9_]*)`?\s+as\s*\(/gi;
  pattern.lastIndex = withAt.index;
  for (let match = pattern.exec(body); match; match = pattern.exec(body)) {
    const open = body.indexOf('(', match.index + match[0].length - 1);
    const inner = balanced(body, open);
    if (inner) {
      found.set(match[1].toLowerCase(), inner);
    }
  }
  return found;
}

const MAX_PROJECTION_HOPS = 6;

/**
 * The projection that decides what a query returns, seen through any `*`.
 *
 * `SELECT * FROM final` is the shape a generated query most often ends in, and
 * its own projection names nothing: the columns are in the CTE. Reading the
 * star literally condemned every one of those as returning the wrong columns.
 * So a transparent projection is followed to its source — a CTE, a subquery, or
 * a real table, where there is nothing further to see and nothing to condemn.
 */
export function resolvedProjection(sql: string, hops = 0): string {
  const projection = outerProjection(sql);
  if (hops >= MAX_PROJECTION_HOPS || !STAR_ONLY.test(projection)) {
    return projection;
  }

  const source = outerSource(sql);
  if (source.startsWith('(')) {
    return resolvedProjection(balanced(source, 0), hops + 1);
  }

  const name = /^`?([A-Za-z_][A-Za-z0-9_]*)`?/.exec(source)?.[1]?.toLowerCase();
  const cte = name ? commonTableExpressions(sql).get(name) : undefined;
  // A star over a physical table forwards columns we cannot see from here, so
  // there is nothing to check rather than something to reject.
  return cte ? resolvedProjection(cte, hops + 1) : projection;
}

/**
 * Does this query return the dataset that was asked for, rather than a check of
 * it? A probe such as `SELECT COUNT(*) FROM (<the real query>)` is valid SQL,
 * runs happily, and mentions every declared column, so only what the outer
 * SELECT projects can tell the two apart.
 */
export function returnsDeclaredColumns(sql: string, columns: Array<string>): boolean {
  if (!columns.length) {
    return true;
  }
  const projection = resolvedProjection(sql).toLowerCase();
  // A star over a table we cannot see into forwards whatever it holds. There is
  // no evidence either way, and refusing on no evidence loses good queries.
  if (STAR_ONLY.test(projection)) {
    return true;
  }
  return columns.some((column) =>
    new RegExp(`\\b${column.toLowerCase().replace(/[^a-z0-9_]/g, '')}\\b`).test(projection),
  );
}

/**
 * The date the generator says the period follows, and the alternatives it saw.
 *
 * Declared in comments rather than inferred, because guessing which of
 * booking_date, travel_date and transaction_date a query means is exactly the
 * judgement the generator has the schema for and we do not.
 */
export function extractDateDeclaration(sql: string): { column?: string; available: Array<string> } {
  const one = (key: string) => {
    const match = new RegExp(`--\\s*${key}\\s*:\\s*([^\\n]+)`, 'i').exec(sql);
    return match ? match[1].trim() : '';
  };
  const clean = (value: string) =>
    value
      .split(',')
      .map((entry) => entry.trim().replace(/^`|`$/g, ''))
      .filter((entry) => COLUMN_NAME.test(entry));

  const column = clean(one('date'))[0];
  const available = [...new Set([...(column ? [column] : []), ...clean(one('dates'))])];
  return { ...(column ? { column } : {}), available };
}

const COLUMN_NAME = /^[A-Za-z_][A-Za-z0-9_]{0,127}$/;

/** Both halves of the dashboard period, bound by the query itself. */
export function bindsDateRange(sql: string): boolean {
  return /@start_date\b/.test(sql) && /@end_date\b/.test(sql);
}

/**
 * A date the reader cannot move off.
 *
 * A literal or a relative window looks right on the day it is written and
 * silently ignores the period control forever after, which is the failure that
 * reads as "the filter does nothing" rather than as a bad query.
 */
export function hasFrozenDateBound(sql: string): boolean {
  const body = sql.replace(/--[^\n]*/g, ' ').replace(/\/\*[\s\S]*?\*\//g, ' ');
  const comparison = /(?:>=|<=|>|<|=|BETWEEN)\s*$/i;
  const frozen = [
    /\bDATE\s*(?:'|")\d{4}-\d{2}-\d{2}/gi,
    /(?:'|")\d{4}-\d{2}-\d{2}(?:'|")/g,
    /\bCURRENT_DATE\s*\(/gi,
    /\bCURRENT_TIMESTAMP\s*\(/gi,
    /\bDATE_SUB\s*\(/gi,
    /\bDATE_TRUNC\s*\(\s*CURRENT_/gi,
  ];
  return frozen.some((pattern) => {
    for (const match of body.matchAll(pattern)) {
      // A date inside a CAST or a label is not a bound; one on the right of a
      // comparison is.
      const before = body.slice(Math.max(0, match.index - 24), match.index);
      if (comparison.test(before.trimEnd() + ' ') || comparison.test(before)) {
        return true;
      }
    }
    return false;
  });
}

/**
 * What is wrong with a query's relationship to the dashboard period.
 *
 * Two of these are fatal and one is not, which is the distinction that matters.
 * A script cannot run at all, and a frozen date bound ignores the period
 * forever while looking correct — both are worth refusing whatever the query is
 * about. Simply not being date-bound is not a fault in the same sense: a card
 * showing current inventory by store has no date to bind, and demanding one
 * would spend a call arguing with a query that was already right.
 */
export function periodFault(sql: string): { fatal: boolean; correction: string } | undefined {
  if (!isSingleStatement(sql)) {
    return {
      fatal: true,
      correction:
        'That answer is a script rather than a query: it declares its own variables or runs more than one statement, and the Lens nests every saved query inside another, which a script cannot be. Return one SELECT statement that reads @start_date and @end_date as parameters, and supply their values in the tool call when you validate it rather than declaring them.',
    };
  }
  if (hasFrozenDateBound(sql)) {
    return {
      fatal: true,
      correction:
        'That query fixes its own date range. Replace the literal or relative window with the named parameters @start_date and @end_date, so the reader can move the period.',
    };
  }
  if (!bindsDateRange(sql)) {
    return {
      fatal: false,
      correction:
        'That query is not bound to the Lens period. Add WHERE DATE(<date column>) >= @start_date AND DATE(<date column>) < @end_date, filtering on the table partitioning column where there is one, and declare it with a "-- date:" line.',
    };
  }
  return undefined;
}

/**
 * Whether a generated dataset can answer a change of period.
 *
 * Checked rather than trusted: asking the model for parameters reduces how
 * often it writes a literal instead, and only reading the query back stops the
 * ones where it did.
 */
export function acceptsDashboardPeriod(sql: string): { ok: boolean; correction?: string } {
  const fault = periodFault(sql);
  return fault ? { ok: false, correction: fault.correction } : { ok: true };
}

/**
 * The column a bound query filters the period on, read from the predicate.
 *
 * A fallback for when the generator bound the range correctly but did not
 * declare which date it used: the predicate is the more reliable of the two,
 * since it is the thing that actually runs.
 */
export function dateColumnFromBinding(sql: string): string | undefined {
  const body = sql.replace(/--[^\n]*/g, ' ');
  const match =
    /(?:DATE|DATETIME|TIMESTAMP)\s*\(\s*`?([A-Za-z_][A-Za-z0-9_]*)`?\s*\)\s*>=\s*@start_date/i.exec(body) ??
    /`?([A-Za-z_][A-Za-z0-9_]*)`?\s*>=\s*@start_date/i.exec(body);
  return match?.[1];
}

/**
 * A query that counts rather than returns: the bounded probe, not the dataset.
 *
 * The generator is asked to check its work by running one, so a probe legitimately
 * appears in the stream. What it must not do is be mistaken for the answer.
 */
export function looksLikeRowCount(sql: string): boolean {
  const projection = resolvedProjection(sql).replace(/\s+/g, ' ').trim();
  // One expression, and that expression is a count.
  if (projection.split(',').length > 1) {
    return false;
  }
  return /^count\s*\(/i.test(projection);
}

/**
 * Which of the queries in one answer is the dataset.
 *
 * AskRichard streams an sql block for every query it runs, and it is asked to
 * run a bounded probe before answering. Keeping the last block therefore hands
 * the probe the win whenever the model checks its work last — which is what
 * being careful looks like. So the blocks are judged rather than ordered:
 * returning the promised columns decides whether it is the dataset at all,
 * taking the dashboard period decides whether it can be moved through time,
 * and a counting query is refused outright.
 */
export function bestDatasetSql(blocks: Array<string>, columns: Array<string>): string | undefined {
  const score = (sql: string) =>
    looksLikeRowCount(sql)
      ? -1
      : (returnsDeclaredColumns(sql, columns) ? 2 : 0) + (acceptsDashboardPeriod(sql).ok ? 1 : 0);

  // Later blocks win ties: a model that revises its query improves it.
  const best = blocks.reduce<{ sql?: string; score: number }>(
    (winner, sql) => {
      const value = score(sql);
      return value >= winner.score ? { sql, score: value } : winner;
    },
    { score: -Infinity },
  );
  // A negative score is a refusal, not a ranking: an answer that is only ever a
  // count has not produced a dataset, and returning it would store the probe.
  return best.score >= 0 ? best.sql : undefined;
}

/**
 * Which of the queries in one answer is the card's.
 *
 * A card promises no columns, so there is nothing to check it against beyond
 * refusing the probe it was asked to run.
 */
export function bestCardSql(blocks: Array<string>): string | undefined {
  const real = blocks.filter((sql) => !looksLikeRowCount(sql));
  return real[real.length - 1] ?? blocks[blocks.length - 1];
}

/**
 * Script variables the query declares for itself, by name.
 *
 * DECLARE is how a BigQuery user makes a parameterised query runnable on its
 * own, so a model asked for @start_date and told to validate its work reaches
 * for it naturally. It is also a multi-statement script rather than an
 * expression, so nothing can wrap it: the reader sees a syntax error from the
 * wrapping rather than anything about the query.
 */
export function declaredVariables(sql: string): Array<string> {
  const body = sql.replace(/--[^\n]*/g, ' ').replace(/\/\*[\s\S]*?\*\//g, ' ');
  return [...body.matchAll(/\bDECLARE\s+([A-Za-z_][A-Za-z0-9_]*)/gi)].map((match) => match[1]);
}

/** Names the dashboard can supply as native query parameters. */
const BOUND_PARAMETERS = ['start_date', 'end_date'];

/**
 * Turn a script that declares the period into a query that takes it.
 *
 * A narrow repair, not a rewrite: the DECLARE statements are removed and the
 * variables they introduced become the parameters the dashboard already binds.
 * Anything else it declared is beyond us — those values would go unbound and
 * the query would fail on a missing parameter instead of a syntax error, which
 * is no better — so that case is left alone for the caller to refuse.
 */
export function parameteriseDeclarations(sql: string): string | undefined {
  const declared = declaredVariables(sql);
  if (!declared.length) {
    return sql;
  }
  if (!declared.every((name) => BOUND_PARAMETERS.includes(name.toLowerCase()))) {
    return undefined;
  }

  // Drop each DECLARE through its terminating semicolon, then read the bare
  // variable names as parameters. Word-bounded and skipping any already
  // prefixed, so an @start_date in the same query is left as it is.
  const withoutDeclarations = sql.replace(/\bDECLARE\s+[A-Za-z_][A-Za-z0-9_]*[^;]*;\s*/gi, '');
  const parameterised = declared.reduce(
    (text, name) => text.replace(new RegExp(`(^|[^@\\w])\\b${name}\\b`, 'g'), `$1@${name}`),
    withoutDeclarations,
  );
  const trimmed = parameterised.trim();
  // One statement, so it can be wrapped. A trailing semicolon is harmless here
  // but not once this sits inside a subquery.
  return trimmed.replace(/;\s*$/, '') || undefined;
}

/**
 * Whether a query is one expression the service can wrap and run.
 *
 * Saved SQL is always nested — in a row cap, in a filter layer, in a date
 * wrapper — so a script is not a stricter style of the same thing, it is a
 * different kind of thing that cannot be used at all.
 */
export function isSingleStatement(sql: string): boolean {
  if (declaredVariables(sql).length) {
    return false;
  }
  const body = sql
    .replace(/--[^\n]*/g, ' ')
    .replace(/\/\*[\s\S]*?\*\//g, ' ')
    .replace(/'[^']*'/g, "''")
    .replace(/"[^"]*"/g, '""');
  // A single trailing semicolon terminates the one statement; anything before
  // the end separates two of them.
  return !/;\s*\S/.test(body);
}

/**
 * Fenced sql blocks in an answer's text.
 *
 * Both prompts ask for the production query as a fenced block, and nothing here
 * was reading them: the only SQL the build saw was whatever the stream labelled
 * `sql`, which is the queries AskRichard executed. When the last thing it
 * executed was the probe it was asked to run, refusing the probe left nothing
 * at all — the answer was sitting in the text the whole time.
 */
export function fencedSqlBlocks(answer: string): Array<string> {
  return [...answer.matchAll(/```(?:sql)?\s*\n([\s\S]*?)```/gi)]
    .map((match) => match[1].trim())
    .filter((block) => /^\s*(?:--|\/\*|with\b|select\b)/i.test(block));
}

/**
 * Why nothing in an answer could be used, in the reader's terms.
 *
 * An answer carrying several queries, all refused, and an answer carrying none
 * are different problems with different fixes, and one message for both left
 * the only way forward as guessing. `columns` is the dataset's declared
 * contract; pass an empty array for a card, which promises none.
 */
export function describeRejection(blocks: Array<string>, columns: Array<string>): string {
  if (!blocks.length) {
    return 'askRichard returned no query at all. Try a narrower scope, or build this dataset on its own.';
  }
  const reasons = blocks.map((sql, index) => {
    const fault = looksLikeRowCount(sql)
      ? 'counts rows instead of returning them'
      : !returnsDeclaredColumns(sql, columns)
        ? `returns ${resolvedProjection(sql).replace(/\s+/g, ' ').trim().slice(0, 60)}`
        : (periodFault(sql)?.correction.slice(0, 60) ?? 'was not usable');
    return `query ${index + 1} ${fault}`;
  });
  return `None of the ${blocks.length} queries returned could be used: ${reasons.join('; ')}`;
}

const PROBE_CORRECTION =
  'Your previous answer returned a check rather than the dataset. Reply with the production query that selects the columns above.';

/** One generation attempt: what came back, and the query picked out of it. */
export type Generated = { answer: string; sql: string; sqlBlocks: Array<string>; streamError: string };

/** What is wrong with a generated dataset query, as corrections to send back. */
export function datasetFaults(sql: string | undefined, blocks: Array<string>, columns: Array<string>): Array<string> {
  if (!sql) {
    // Blocks arrived but none was a dataset: every query in that answer counted
    // rows. Worth saying so, where an answer carrying no SQL at all is not
    // something a correction can fix.
    return blocks.length ? [PROBE_CORRECTION] : [];
  }
  return [
    returnsDeclaredColumns(sql, columns) ? '' : PROBE_CORRECTION,
    acceptsDashboardPeriod(sql).correction ?? '',
  ].filter(Boolean);
}

/**
 * The better of two attempts at one dataset.
 *
 * Returning the right columns is the thing that decides whether a dataset
 * exists at all; taking the dashboard period is what decides whether it can be
 * moved through time. So a query with the columns beats one without, whatever
 * else improved, and the period only breaks the tie.
 */
export function preferredDataset(first: Generated, second: Generated, columns: Array<string>): Generated {
  const score = (candidate: Generated) => {
    if (!candidate.sql || candidate.streamError) {
      return -1;
    }
    return (
      (returnsDeclaredColumns(candidate.sql, columns) ? 2 : 0) + (acceptsDashboardPeriod(candidate.sql).ok ? 1 : 0)
    );
  };
  // Ties go to the retry: it answered the correction it was given.
  return score(second) >= score(first) ? second : first;
}
